import { useState, useCallback, useMemo, useContext } from 'react';
import {
  HELP_DESK_STATUSES,
  HELP_DESK_TICKET_BADGES,
  DEFAULT_RESOLUTION_TIMEOUT_HOURS
} from '@ubeya/shared/constants/helpDesk';
import useToaster from '@ubeya/shared/hooks/useToaster';
import useHelpDeskUpdateTicket from '@ubeya/shared/hooks/account/useHelpDeskUpdateTicket';
import useHelpDeskAccountSettings from '@ubeya/shared/hooks/account/useHelpDeskAccountSettings';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { useHelpDeskAnalytics } from '../helpDeskAnalytics';

const useHelpDeskTicketStatusActions = ({ ticket, accountId, unreadCount = 0, isManuallyUnread }) => {
  const { t } = useContext(ConfigContext);
  const { addSuccess, addResponseError } = useToaster();
  const { trackHelpDeskTicketEvent } = useHelpDeskAnalytics();

  const { updateTicket, isLoading: updateLoading } = useHelpDeskUpdateTicket({ accountId, ticketId: ticket.id });

  const updateMutation = useMemo(
    () => ({
      mutate: (body, onSuccess) => updateTicket(body, { onSuccess, onError: addResponseError }),
      isLoading: updateLoading
    }),
    [updateTicket, addResponseError, updateLoading]
  );

  const isClosed = ticket.status === HELP_DESK_STATUSES.CLOSED;
  const isResolved = !!ticket.resolvedAt;

  const { settings } = useHelpDeskAccountSettings({ accountId });
  const resolutionTimeoutHours = settings?.resolutionTimeoutHours ?? DEFAULT_RESOLUTION_TIMEOUT_HOURS;

  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showResolveConfirm, setShowResolveConfirm] = useState(false);

  // Tracks whether the menu's custom snooze picker is open so the DropDown can widen its panel.
  // The child owns the actual UI state and signals up via onCustomPickerVisibleChange.
  const [isMenuCustomPickerVisible, setIsMenuCustomPickerVisible] = useState(false);

  const trackSnoozeEvent = useCallback(
    ({ eventName, snoozedUntil }) => {
      trackHelpDeskTicketEvent({
        eventName,
        context: 'Help Desk ticket actions',
        ticket,
        snoozedUntil
      });
    },
    [ticket, trackHelpDeskTicketEvent]
  );

  const trackResolvedEvent = useCallback(() => {
    if (!ticket) return;

    trackHelpDeskTicketEvent({
      eventName: 'Help Desk - Admin Resolved Ticket',
      context: 'Help Desk ticket actions',
      ticket
    });
  }, [ticket, trackHelpDeskTicketEvent]);

  const trackTicketStatusEvent = useCallback(
    (eventName, ticketStatus) => {
      trackHelpDeskTicketEvent({
        eventName,
        context: 'Help Desk ticket actions',
        ticket,
        ticketStatus
      });
    },
    [ticket, trackHelpDeskTicketEvent]
  );

  const handleResolve = useCallback(() => {
    setShowResolveConfirm(true);
  }, []);

  const handleResolveConfirm = useCallback(() => {
    updateMutation.mutate({ resolve: true, snoozedUntil: null }, () => {
      trackResolvedEvent();
      addSuccess(t('help_desk_ticket_resolved'));
    });
    setShowResolveConfirm(false);
  }, [updateMutation, trackResolvedEvent, addSuccess, t]);

  const handleSnooze = useCallback(
    (snoozedUntil) => {
      updateMutation.mutate({ status: HELP_DESK_STATUSES.SNOOZED, snoozedUntil }, () => {
        trackSnoozeEvent({ eventName: 'Help Desk - Admin Snoozed Ticket', snoozedUntil });
        addSuccess(t('help_desk_ticket_snoozed'));
      });
    },
    [updateMutation, trackSnoozeEvent, addSuccess, t]
  );

  const handleUnsnooze = useCallback(() => {
    updateMutation.mutate({ status: HELP_DESK_STATUSES.OPEN, snoozedUntil: null }, () => {
      trackSnoozeEvent({ eventName: 'Help Desk - Admin Unsnoozed Ticket', snoozedUntil: null });
      addSuccess(t('help_desk_ticket_unsnoozed'));
    });
  }, [updateMutation, trackSnoozeEvent, addSuccess, t]);

  const handleClose = useCallback(() => {
    updateMutation.mutate({ status: HELP_DESK_STATUSES.CLOSED, snoozedUntil: null }, () => {
      trackTicketStatusEvent('Help Desk - Admin Closed Ticket', HELP_DESK_STATUSES.CLOSED);
      addSuccess(t('help_desk_ticket_closed'));
    });
    setShowCloseConfirm(false);
  }, [updateMutation, trackTicketStatusEvent, addSuccess, t]);

  const handleReopen = useCallback(() => {
    updateMutation.mutate({ status: HELP_DESK_STATUSES.OPEN, snoozedUntil: null }, () => {
      trackTicketStatusEvent('Help Desk - Admin Reopened Ticket', HELP_DESK_STATUSES.OPEN);
      addSuccess(t('help_desk_ticket_reopened'));
    });
  }, [updateMutation, trackTicketStatusEvent, addSuccess, t]);

  const handleUnresolve = useCallback(() => {
    updateMutation.mutate({ resolve: false, snoozedUntil: null }, () => {
      addSuccess(t('help_desk_ticket_unresolved'));
    });
  }, [updateMutation, addSuccess, t]);

  const handleCloseTicketRequest = useCallback(() => {
    setShowCloseConfirm(true);
  }, []);

  const isUnread = unreadCount > 0 || (isManuallyUnread ?? ticket.isManuallyUnread ?? false);

  const handleToggleRead = useCallback(() => {
    updateMutation.mutate({ isRead: isUnread });
  }, [updateMutation, isUnread]);

  const isPending = ticket.badge === HELP_DESK_TICKET_BADGES.PENDING;

  const handleTogglePending = useCallback(() => {
    updateMutation.mutate({ badge: isPending ? null : HELP_DESK_TICKET_BADGES.PENDING }, () => {
      addSuccess(t(isPending ? 'help_desk_ticket_unmarked_pending' : 'help_desk_ticket_marked_pending'));
    });
  }, [updateMutation, isPending, addSuccess, t]);

  return {
    isClosed,
    isResolved,
    isLoading: updateMutation.isLoading,
    resolutionTimeoutHours,
    showCloseConfirm,
    setShowCloseConfirm,
    showResolveConfirm,
    setShowResolveConfirm,
    isMenuCustomPickerVisible,
    setIsMenuCustomPickerVisible,
    handleResolve,
    handleResolveConfirm,
    handleSnooze,
    handleUnsnooze,
    handleUnresolve,
    handleClose,
    handleReopen,
    handleCloseTicketRequest,
    handleTogglePending,
    isUnread,
    handleToggleRead
  };
};

export default useHelpDeskTicketStatusActions;
